import { formatOrderQuantity } from '@/lib/order-denomination'
import { formatSymbol } from '@/lib/utils/format-util'
import { humanizeReason } from '@/lib/utils/humanize-reason'
import { AlgoStatus } from '@/types/generated/algo'
import { OrderStatus } from '@/types/generated/common/trading'
import {
  type AlgoLifecycleNotification,
  type DeadmanArmRefused,
  DeadmanArmRefusedReason,
  type DeadmanFired,
  type OrderExecutionError,
  type OrderResponse,
  type OrderValidationError,
  type SecurityAlert,
  type ServiceNotification,
  type TradeReverted,
  type VenueNotice,
} from '@/types/generated/notifications'

export type NotificationCategory =
  | 'order_placed'
  | 'order_filled'
  | 'order_cancelled'
  | 'order_error'
  | 'algo'
  | 'research'
  | 'security'
  | 'unknown'

export type NotificationSeverity = 'success' | 'warning' | 'error' | 'info'

export interface ClassifiedNotification {
  category: NotificationCategory
  severity: NotificationSeverity
  title: string
  message: string
  symbol?: string
}

function classifyOrderResponse(response: OrderResponse): ClassifiedNotification {
  const symbol = response.symbol

  if (response.errorMessage) {
    return {
      category: 'order_error',
      severity: 'error',
      title: 'Order Rejected',
      message: humanizeReason(response.errorMessage),
      symbol,
    }
  }

  switch (response.status) {
    case OrderStatus.ORDER_STATUS_FILLED:
      return {
        category: 'order_filled',
        severity: 'success',
        title: 'Order Filled',
        message: `Filled ${formatOrderQuantity(response, response.filledQuantity ?? response.quantity)} ${formatSymbol(symbol)}`,
        symbol,
      }
    case OrderStatus.ORDER_STATUS_PARTIALLY_FILLED:
      return {
        category: 'order_filled',
        severity: 'success',
        title: 'Order Partially Filled',
        message: `Partially filled ${formatOrderQuantity(response, response.filledQuantity ?? response.quantity)} ${formatSymbol(symbol)}`,
        symbol,
      }
    case OrderStatus.ORDER_STATUS_CANCELLED:
      return {
        category: 'order_cancelled',
        severity: 'warning',
        title: 'Order Cancelled',
        message: `${formatSymbol(symbol)} order cancelled`,
        symbol,
      }
    case OrderStatus.ORDER_STATUS_REJECTED:
      return {
        category: 'order_error',
        severity: 'error',
        title: 'Order Rejected',
        message: `${formatSymbol(symbol)} order rejected`,
        symbol,
      }
    case OrderStatus.ORDER_STATUS_OPEN:
      return {
        category: 'order_placed',
        severity: 'info',
        title: 'Order Open',
        message: `${formatSymbol(symbol)} order open · qty ${formatOrderQuantity(response, response.quantity)}`,
        symbol,
      }
    case OrderStatus.ORDER_STATUS_PENDING:
      return {
        category: 'order_placed',
        severity: 'info',
        title: 'Order Pending',
        message: `${formatSymbol(symbol)} order pending · qty ${formatOrderQuantity(response, response.quantity)}`,
        symbol,
      }
    default:
      return {
        category: 'order_placed',
        severity: 'info',
        title: 'Order Update',
        message: formatSymbol(symbol),
        symbol,
      }
  }
}

function classifyOrderValidationError(error: OrderValidationError): ClassifiedNotification {
  return {
    category: 'order_error',
    severity: 'warning',
    title: 'Order Validation Failed',
    message: humanizeReason(error.errorMessage),
    symbol: error.symbol,
  }
}

function classifyOrderExecutionError(error: OrderExecutionError): ClassifiedNotification {
  return {
    category: 'order_error',
    severity: 'error',
    title: 'Order Execution Failed',
    message: humanizeReason(error.errorMessage),
  }
}

function algoSeverity(status: AlgoStatus): NotificationSeverity {
  switch (status) {
    case AlgoStatus.ALGO_STATUS_COMPLETED:
    case AlgoStatus.ALGO_STATUS_PENDING:
    case AlgoStatus.ALGO_STATUS_IN_PROGRESS:
      return 'success'
    case AlgoStatus.ALGO_STATUS_FAILED:
      return 'error'
    case AlgoStatus.ALGO_STATUS_CANCELLED:
    case AlgoStatus.ALGO_STATUS_CANCELLING:
    case AlgoStatus.ALGO_STATUS_CANCEL_REQUESTED:
    case AlgoStatus.ALGO_STATUS_TRANSITIONING:
    case AlgoStatus.ALGO_STATUS_UNTRIGGERED:
    default:
      return 'warning'
  }
}

function classifyAlgoLifecycle(algo: AlgoLifecycleNotification): ClassifiedNotification {
  return {
    category: 'algo',
    severity: algoSeverity(algo.status),
    title: algo.message,
    message: '',
    symbol: algo.symbol,
  }
}

function classifySecurityAlert(alert: SecurityAlert): ClassifiedNotification {
  return {
    category: 'security',
    severity: 'error',
    title: 'Security Alert',
    message: alert.message,
  }
}

function classifyVenueNotice(notice: VenueNotice): ClassifiedNotification {
  // 'unknown' is always visible in history (never filtered out), which fits a notice that
  // applies to every venue and has no category chip of its own.
  return {
    category: 'unknown',
    severity: 'warning',
    title: 'Venue Notice',
    message: notice.message,
  }
}

// ENG-1063 venue layer (the options market maker): what a person needs to hear about.
// Titles are i18n keys under `notifications.venue`; the history panel translates them.

const VENUE_TITLE_PREFIX = 'notifications.venue.'

/**
 * The key under `notifications` for a title that is an i18n key (the venue-layer
 * titles below), or `null` for a title that is already display text.
 */
export function notificationTitleKey(title: string): string | null {
  return title.startsWith(VENUE_TITLE_PREFIX) ? title.slice('notifications.'.length) : null
}

function classifyDeadmanFired(fired: DeadmanFired): ClassifiedNotification {
  // The venue cancelled the account's open orders because the algo stopped heartbeating.
  // A failed cancel is worse: orders may still be resting with nothing watching them.
  return fired.cancelOk
    ? {
        category: 'algo',
        severity: 'warning',
        title: `${VENUE_TITLE_PREFIX}deadmanFired`,
        message: fired.label,
      }
    : {
        category: 'algo',
        severity: 'error',
        title: `${VENUE_TITLE_PREFIX}deadmanCancelFailed`,
        message: [fired.errorCode, 'check open orders'].filter(Boolean).join(' · '),
      }
}

const DEADMAN_REFUSED_REASON: Partial<Record<DeadmanArmRefusedReason, string>> = {
  [DeadmanArmRefusedReason.DEADMAN_ARM_REFUSED_REASON_OPEN_ORDER_CAP]: 'Open-order cap reached',
  [DeadmanArmRefusedReason.DEADMAN_ARM_REFUSED_REASON_STALE_GENERATION]:
    'Superseded by a newer start',
  [DeadmanArmRefusedReason.DEADMAN_ARM_REFUSED_REASON_RESERVATION_STORE_UNAVAILABLE]:
    'Reservation store unavailable',
}

function classifyDeadmanArmRefused(refused: DeadmanArmRefused): ClassifiedNotification {
  const reason = DEADMAN_REFUSED_REASON[refused.reason] ?? 'Refused by the venue layer'
  const cap =
    refused.reason === DeadmanArmRefusedReason.DEADMAN_ARM_REFUSED_REASON_OPEN_ORDER_CAP
      ? ` (${refused.reservedTotal} of ${refused.cap})`
      : ''
  return {
    category: 'algo',
    severity: 'warning',
    title: `${VENUE_TITLE_PREFIX}deadmanArmRefused`,
    message: `${reason}${cap}`,
  }
}

function classifyTradeReverted(trade: TradeReverted): ClassifiedNotification {
  // Derive: a fill whose settlement batch failed — published instead of the fill.
  return {
    category: 'order_error',
    severity: 'error',
    title: `${VENUE_TITLE_PREFIX}tradeReverted`,
    message: `${formatSymbol(trade.instrumentName)} fill reverted · ${trade.batchStatus}`,
    symbol: trade.instrumentName,
  }
}

/**
 * ENG-1063 venue-layer state the market-maker algo runs on — socket up/down, rate-limit
 * budgets, venue capabilities, margin-simulation replies, a dead-man switch being armed.
 * Published per account every few minutes and meant for the algo, not a person: they used
 * to fall through to the generic "Notification" row and flood the bell. Not shown.
 */
export function isSilentNotification(notification: ServiceNotification): boolean {
  return Boolean(
    notification.venueConnectionState ||
    notification.venueRateLimits ||
    notification.venueCapabilities ||
    notification.marginSimulated ||
    notification.deadmanArmed
  )
}

/**
 * True for the ENG-1063 market-maker venue-layer variants. They feed
 * `venueTelemetryAtom`; those that aren't `isSilentNotification` also reach the history.
 */
export function isVenueTelemetry(notification: ServiceNotification): boolean {
  return !!(
    notification.venueConnectionState ||
    notification.venueRateLimits ||
    notification.deadmanArmed ||
    notification.deadmanFired ||
    notification.tradeReverted ||
    notification.deadmanArmRefused ||
    notification.venueCapabilities ||
    notification.marginSimulated
  )
}

/**
 * Single source of truth for how a ServiceNotification is categorized, severity-scored,
 * and titled — the toast dispatcher and the history panel both consume this so they
 * can't drift from each other. `message` is a proto3 oneof, so at most one of the
 * checks below ever matches regardless of order.
 */
export function classifyNotification(notification: ServiceNotification): ClassifiedNotification {
  if (notification.orderValidationError)
    return classifyOrderValidationError(notification.orderValidationError)
  if (notification.orderExecutionError)
    return classifyOrderExecutionError(notification.orderExecutionError)
  if (notification.algoLifecycle) return classifyAlgoLifecycle(notification.algoLifecycle)
  if (notification.orderResponse) return classifyOrderResponse(notification.orderResponse)
  if (notification.securityAlert) return classifySecurityAlert(notification.securityAlert)
  if (notification.venueNotice) return classifyVenueNotice(notification.venueNotice)
  if (notification.deadmanFired) return classifyDeadmanFired(notification.deadmanFired)
  if (notification.deadmanArmRefused)
    return classifyDeadmanArmRefused(notification.deadmanArmRefused)
  if (notification.tradeReverted) return classifyTradeReverted(notification.tradeReverted)
  // No oneof variant set — shouldn't happen, but never silently mislabel this as an
  // order_error: that would drop it from history whenever the "Errors" filter is off.
  return { category: 'unknown', severity: 'warning', title: 'Notification', message: '' }
}
